import type { ListBuilderColumn } from '../../types';
import {
	RECOMMENDATION_LEVEL_VALUE_MAPPING,
	STAR_RATING_VALUE_MAPPING,
} from './shared';

/**
 * Item # column showing the best available identifier:
 * externalId2 if present, else externalId, else the database _id.
 * The displayItemNumber field is computed in ClientItemHistory.tsx.
 */
export const createItemNumberColumn = (): ListBuilderColumn => ({
	id: 'itemNumber',
	fields: [
		{
			id: 'item-number-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'displayItemNumber' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-item-number', text: 'Item #' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Shared item column definition with link to item page
 */
export const createItemColumn = (): ListBuilderColumn => ({
	id: 'item',
	fields: [
		{
			id: 'name-field',
			link: {
				type: 'object',
				enabled: true,
				idField: 'itemId',
				objectTable: 'item',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'itemName', objectTable: 'item' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'static',
					staticColor: { text: '#3b82f6' },
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'category-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'category' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-category',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'manufacturer-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'manufacturer', objectTable: 'item' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h1', text: 'Item' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'medium' },
	verticalAlignment: 'middle',
});

/**
 * Item column with custom category field ID suffix
 * Used in due-for-reorder and at-risk lists
 */
export const createItemColumnWithSuffix = (
	suffix: string,
): ListBuilderColumn => ({
	id: 'item',
	fields: [
		{
			id: 'name-field',
			link: {
				type: 'object',
				enabled: true,
				idField: 'itemId',
				objectTable: 'item',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'itemName', objectTable: 'item' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'static',
					staticColor: { text: '#3b82f6' },
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: `category-field-${suffix}`,
			dataConfig: {
				type: 'field',
				field: { fieldName: 'category' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: `line-break-category-${suffix}`,
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'manufacturer-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'manufacturer', objectTable: 'item' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h1', text: 'Item' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'medium' },
	verticalAlignment: 'middle',
});

/**
 * Shared chart column definition
 */
export const createChartColumn = (idSuffix: string): ListBuilderColumn => ({
	id: `chart${idSuffix}`,
	fields: [
		{
			id: `chart${idSuffix.toLowerCase()}-field`,
			dataConfig: {
				type: 'field',
				field: { fieldName: 'monthlyData' },
				defaultValue: '[]',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'custom',
			customComponent: { id: 'client-item-history-trend-chart' },
		},
	],
	headers: [{ id: `h-chart${idSuffix.toLowerCase()}`, text: '12 Month Trend' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'middle',
});

/**
 * Basic chart column without suffix (for previously purchased list)
 */
export const createBasicChartColumn = (): ListBuilderColumn => ({
	id: 'chart',
	fields: [
		{
			id: 'chart-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'monthlyData' },
				defaultValue: '[]',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'custom',
			customComponent: { id: 'client-item-history-trend-chart' },
		},
	],
	headers: [{ id: 'h4', text: '12 Month Trend' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'middle',
});

/**
 * Purchase info column — renders date (X days ago), frequency, and invoice link
 * via the client-item-history-purchase-info custom component.
 * Used by all three tabs (Previously Purchased, Reorder, At Risk).
 */
export const createPurchaseInfoColumn = (): ListBuilderColumn => ({
	id: 'purchaseInfo',
	fields: [
		{
			id: 'purchase-info-custom',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'lastPurchasedDate' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'custom',
			customComponent: { id: 'client-item-history-purchase-info' },
		},
	],
	headers: [
		{ id: 'h-last-purchased', text: 'Last Purchased' },
		{ id: 'h-frequency', text: 'Frequency' },
		{ id: 'h-last-invoice', text: 'Last Invoice' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'small' },
	verticalAlignment: 'middle',
});

/**
 * Financial metrics column for previously purchased list
 */
export const createFinancialMetricsColumn = (): ListBuilderColumn => ({
	id: 'financialMetrics',
	fields: [
		{
			id: 'total-spent-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'totalSpent' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
		{
			id: 'line-break-financial',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'avg-unit-price-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgUnitPrice' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h-financial-1', text: 'Total Spent' },
		{ id: 'h-financial-2', text: 'Avg Unit Price' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Average per order column for previously purchased list
 */
export const createAvgPerOrderColumn = (): ListBuilderColumn => ({
	id: 'avgPerOrder',
	fields: [
		{
			id: 'avg-qty-per-order-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgQtyPerPurchase' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
				formatOptions: { decimalPlaces: 1 },
			},
			displayStyle: 'number',
		},
		{
			id: 'line-break-avg-order',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'avg-sales-per-order-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgSalesPerPurchase' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h-avg-order-1', text: 'Avg Qty/Order' },
		{ id: 'h-avg-order-2', text: 'Avg $/Order' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Six month comparison column for previously purchased list
 */
export const createSixMonthComparisonColumn = (): ListBuilderColumn => ({
	id: 'sixMonthComparison',
	fields: [
		{
			id: 'twelve-month-total-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'quantityLast12Months' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-twelve-month',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'last-six-months-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'lastSixMonths' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-six-month',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'previous-six-months-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'previousSixMonths' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h-twelve-month', text: '12-Mo Total' },
		{ id: 'h-six-month-1', text: 'Last 6 Mo' },
		{ id: 'h-six-month-2', text: 'Prev 6 Mo' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Reorder info column for due-for-reorder list
 */
export const createReorderInfoColumn = (): ListBuilderColumn => ({
	id: 'reorderInfo',
	fields: [
		{
			id: 'avg-interval-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgPurchaseIntervalDays' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-reorder-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'days-since-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'daysSinceLastPurchase' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-reorder-2',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'days-overdue-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'daysOverdue' },
				emptyValue: '-',
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'conditional',
					rules: [
						{
							condition: {
								field: 'daysOverdue',
								operator: 'gt',
								value: 0,
							},
							color: { text: '#ef4444' },
						},
					],
					defaultColor: { text: '#9ca3af' },
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h-reorder-1', text: 'Avg. Interval (days)' },
		{ id: 'h-reorder-2', text: 'Days Since Purchase' },
		{ id: 'h-reorder-3', text: 'Days Overdue' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'medium' },
	verticalAlignment: 'middle',
});

/**
 * Purchase info column for due-for-reorder list — delegates to shared createPurchaseInfoColumn
 */
export const createReorderPurchaseInfoColumn = (): ListBuilderColumn =>
	createPurchaseInfoColumn();

/**
 * Average per purchase column for due-for-reorder list
 */
export const createAvgPerPurchaseColumn = (): ListBuilderColumn => ({
	id: 'avgPerPurchase',
	fields: [
		{
			id: 'avg-qty-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgQtyPerPurchase' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
				formatOptions: { decimalPlaces: 1 },
			},
			displayStyle: 'number',
		},
		{
			id: 'line-break-avg-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'avg-sales-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgSalesPerPurchase' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h-avg-1', text: 'Avg Qty/Order' },
		{ id: 'h-avg-2', text: 'Avg $/Order' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Six month comparison column for due-for-reorder list
 */
export const createReorderSixMonthComparisonColumn = (): ListBuilderColumn => ({
	id: 'sixMonthComparisonReorder',
	fields: [
		{
			id: 'twelve-month-qty-reorder-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'quantityLast12Months' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-twelve-month-reorder',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'twelve-month-sales-reorder-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'totalSpent' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
		{
			id: 'line-break-twelve-month-sales-reorder',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'last-six-months-reorder-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'lastSixMonths' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-six-month-reorder',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'previous-six-months-reorder-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'previousSixMonths' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h-twelve-month-qty-reorder', text: '12-Mo Qty' },
		{ id: 'h-twelve-month-sales-reorder', text: '12-Mo Sales' },
		{ id: 'h-six-month-reorder-1', text: 'Last 6 Mo' },
		{ id: 'h-six-month-reorder-2', text: 'Prev 6 Mo' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Purchase info column for at-risk list — delegates to shared createPurchaseInfoColumn
 */
export const createAtRiskPurchaseInfoColumn = (): ListBuilderColumn =>
	createPurchaseInfoColumn();

/**
 * Quantities column for at-risk list
 */
export const createAtRiskQuantitiesColumn = (): ListBuilderColumn => ({
	id: 'quantities',
	fields: [
		{
			id: 'twelve-month-qty-atrisk-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'quantityLast12Months' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-twelve-atrisk',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'last-six-months-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'lastSixMonths' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-3',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'previous-six-months-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'previousSixMonths' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h-twelve-mo-qty-atrisk', text: '12-Mo Qty' },
		{ id: 'h4', text: 'Last 6 Mo' },
		{ id: 'h5', text: 'Prev 6 Mo' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Financial column for at-risk list
 */
export const createAtRiskFinancialColumn = (): ListBuilderColumn => ({
	id: 'financialAtRisk',
	fields: [
		{
			id: 'total-spent-atrisk-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'totalSpent' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
		{
			id: 'line-break-financial-atrisk',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'avg-unit-price-atrisk-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgUnitPrice' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h-financial-atrisk-1', text: 'Total Spent' },
		{ id: 'h-financial-atrisk-2', text: 'Avg Unit Price' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Average per order column for at-risk list
 */
export const createAtRiskAvgPerOrderColumn = (): ListBuilderColumn => ({
	id: 'avgPerOrderAtRisk',
	fields: [
		{
			id: 'avg-qty-atrisk-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgQtyPerPurchase' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
				formatOptions: { decimalPlaces: 1 },
			},
			displayStyle: 'number',
		},
		{
			id: 'line-break-avg-atrisk',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'avg-sales-atrisk-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgSalesPerPurchase' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h-avg-atrisk-1', text: 'Avg Qty/Order' },
		{ id: 'h-avg-atrisk-2', text: 'Avg $/Order' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Recommendation level column for recommended list
 */
export const createRecommendationLevelColumn = (): ListBuilderColumn => ({
	id: 'recommendationLevel',
	fields: [
		{
			id: 'recommendation-badge',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'recommendationLevel' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'value-mapping' },
				valueMapping: [...RECOMMENDATION_LEVEL_VALUE_MAPPING],
			},
			displayStyle: 'badge',
		},
	],
	headers: [{ id: 'h-recommendation', text: 'Recommendation' }],
	sortable: false,
	alignment: 'center',
	widthConfig: { mode: 'fixed', value: '120px' },
	verticalAlignment: 'middle',
});

/**
 * Create a star rating score column
 */
export const createScoreColumn = (
	id: string,
	starsField: string,
	scoreField: string,
	percentField: string,
	template: string,
	headerText: string,
): ListBuilderColumn => ({
	id,
	fields: [
		{
			id: `${id}-stars`,
			dataConfig: {
				type: 'field',
				field: { fieldName: starsField },
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'value-mapping' },
				valueMapping: [...STAR_RATING_VALUE_MAPPING],
			},
			displayStyle: 'text',
		},
		{
			id: `${id}-line-break`,
			displayStyle: 'line_break',
		},
		{
			id: `${id}-text`,
			dataConfig: {
				type: 'template',
				template,
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: `h-${id}`, text: headerText }],
	sortable: false,
	alignment: 'center',
	widthConfig: { mode: 'fixed', value: '100px' },
	verticalAlignment: 'middle',
});
